<template>
  <div class="app-container pb-50px">
    <el-card shadow="never">
      <el-page-header
        :content="identity ? '编辑子账户' : '新增子账户'"
        @back="$router.back()"
      ></el-page-header>
    </el-card>
    <el-card class="mt-16px" v-loading="loading" shadow="never">
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        size="small"
        label-width="100px"
      >
        <div class="section-title">账号设置</div>
        <el-form-item label="登录账号" prop="username">
          <el-input
            v-model="form.username"
            style="width: 480px"
            show-word-limit
            placeholder="请输入登录账号"
          ></el-input>
        </el-form-item>
        <el-form-item label="客户名称" prop="name">
          <el-input
            v-model="form.name"
            style="width: 480px"
            show-word-limit
            placeholder="请输入客户名称"
          ></el-input>
        </el-form-item>
        <el-form-item label="联系邮箱" prop="email">
          <el-input
            v-model="form.email"
            style="width: 480px"
            placeholder="请输入联系邮箱"
          ></el-input>
        </el-form-item>
        <el-form-item label="联系电话" prop="phone">
          <el-input
            v-model="form.phone"
            style="width: 480px"
            placeholder="请输入联系电话"
          ></el-input>
        </el-form-item>
        <template v-if="!identity">
          <el-form-item label="登录密码" prop="password">
            <el-input
              v-model="form.password"
              style="width: 480px"
              show-password
              clearable
              placeholder="请输入登录密码"
            ></el-input>
          </el-form-item>
          <el-form-item label="确认密码" prop="password_confirm">
            <el-input
              v-model="form.password_confirm"
              style="width: 480px"
              show-password
              clearable
              placeholder="请再次输入登录密码"
            ></el-input>
          </el-form-item>
        </template>
        <el-form-item label="绑定站点" prop="site_id">
          <el-select
            v-model="form.site_id"
            filterable
            clearable
            placeholder="请选择可访问站点"
            style="width: 480px"
          >
            <el-option
              v-for="item in siteOptions"
              :key="item.id"
              :label="`${item.name}（${item.url}）`"
              :value="item.id"
            ></el-option>
          </el-select>
          <div class="form-tips">子账户只能查看绑定站点的数据。</div>
        </el-form-item>
        <el-form-item label="账号状态" prop="status">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="正常"
            inactive-text="停用"
          />
        </el-form-item>

        <div class="section-title section-title--permissions">页面权限</div>
        <el-form-item label="可见页面" prop="page_permissions">
          <div class="permission-panel">
            <el-checkbox-group v-model="form.page_permissions">
              <div
                class="permission-row"
                v-for="item in pagePermissionOptions"
                :key="item.value"
              >
                <el-checkbox :label="item.value">
                  <span class="permission-row__name">{{ item.label }}</span>
                  <span class="permission-row__desc">{{ item.desc }}</span>
                </el-checkbox>
              </div>
            </el-checkbox-group>
          </div>
          <div class="form-tips">
            勾选后子账户端显示对应页面；工作台只展示已授权的数据项。
          </div>
        </el-form-item>
      </el-form>
    </el-card>
    <footer-wrap>
      <el-button size="small" @click="$router.back()">取 消</el-button>
      <el-button v-if="canSave" type="primary" size="small" @click="onSubmit">
        保 存
      </el-button>
    </footer-wrap>
  </div>
</template>

<script>
import FooterWrap from "@/components/footer-wrap/index.vue";
import {
  apiAddCustomer,
  apiCustomerSiteOptions,
  apiDetailCustomer,
  apiEditCustomer,
} from "@/api/customer-admin";

const defaultForm = () => ({
  id: "",
  username: "",
  name: "",
  email: "",
  phone: "",
  password: "",
  password_confirm: "",
  site_id: "",
  status: 1,
  page_permissions: ["clue", "access", "rank"],
});

const pagePermissionOptions = [
  {
    value: "clue",
    label: "询盘线索",
    desc: "允许查看绑定站点的询盘线索",
  },
  {
    value: "access",
    label: "访问数据",
    desc: "允许查看绑定站点的 Google 访问数据",
  },
  {
    value: "rank",
    label: "排名数据",
    desc: "允许查看绑定站点的 SEO 关键词排名",
  },
  {
    value: "effect_report",
    label: "效果月报",
    desc: "允许查看已发布的站点运营效果月报",
  },
];

export default {
  components: {
    FooterWrap,
  },
  data() {
    const passwordConfirmValidator = (rule, value, callback) => {
      if (this.form.password && value !== this.form.password) {
        callback(new Error("两次输入密码不一致"));
        return;
      }
      callback();
    };
    return {
      identity: null,
      loading: false,
      siteOptions: [],
      pagePermissionOptions,
      form: defaultForm(),
      rules: {
        username: [
          { required: true, trigger: "blur", message: "请输入登录账号" },
        ],
        name: [{ required: true, trigger: "blur", message: "请输入客户名称" }],
        password: [
          { required: true, trigger: "blur", message: "请输入登录密码" },
          {
            min: 6,
            max: 64,
            trigger: "blur",
            message: "密码长度须在6-64位字符",
          },
        ],
        password_confirm: [
          { required: true, trigger: "blur", message: "请输入确认密码" },
          { validator: passwordConfirmValidator, trigger: "blur" },
        ],
      },
    };
  },
  created() {
    this.identity = this.$route.query.id || null;
    this.getOptions();
    this.identity && this.getDetail();
  },
  computed: {
    canSave() {
      return this.identity
        ? this.$hasPermission("system.customer", "edit")
        : this.$hasPermission("system.customer", "create");
    },
  },
  methods: {
    getOptions() {
      apiCustomerSiteOptions().then((resp) => {
        this.siteOptions = resp || [];
      });
    },
    getDetail() {
      this.loading = true;
      apiDetailCustomer({ id: this.identity })
        .then((resp) => {
          this.form = {
            ...defaultForm(),
            ...(resp || {}),
            site_id: ((resp || {}).site_ids || [])[0] || "",
            page_permissions:
              (resp || {}).page_permissions || defaultForm().page_permissions,
          };
          this.rules.password = [];
          this.rules.password_confirm = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        const params = {
          ...this.form,
          site_ids: this.form.site_id ? [this.form.site_id] : [],
        };
        const api = this.identity
          ? apiEditCustomer({ ...params, id: this.identity })
          : apiAddCustomer(params);
        api.then(() => {
          this.$message({
            type: "success",
            message: "保存成功！",
          });
          this.$router.back();
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.section-title {
  margin: 2px 0 18px;
  color: #1f2937;
  font-size: 15px;
  font-weight: 600;

  &--permissions {
    margin-top: 26px;
  }
}

.permission-panel {
  width: 640px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.permission-row {
  min-height: 48px;
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
  }

  &__name {
    display: inline-block;
    width: 90px;
    color: #303133;
  }

  &__desc {
    color: #909399;
    font-size: 12px;
  }
}

.form-tips {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
</style>
